import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, from, of, switchMap } from "rxjs";
import type { FeedbackTemplateRequest } from "src/app/cbta-web/feedback/feedback.types";
import { Roles } from "src/app/models/enum";
import {
	APIResponse,
	CompleteRatingFilesDto,
	LDAPSignOff,
	PilotDataFilesDto,
	SigInput,
	StagingData,
	TS1FormRequestJSON,
} from "src/app/models/models";
import { prepareProfileImageFile } from "../utils/profile-image.utils";
import { ApiHandlerService } from "./api-handler.service";
import { RoleService } from "./role.service";

@Injectable({
	providedIn: "root",
})
export class HttpService {
	constructor(
		private apiHandlerService: ApiHandlerService,
		private http: HttpClient,
		private roleService: RoleService,
	) {}

	// AUTH
	getMe(): Observable<any> {
		return this.apiHandlerService.getWithoutSnackbarError(`/auth/me`, this.getRequestHeader(), null);
	}

	getSession(): Observable<any> {
		return this.apiHandlerService.getWithoutSnackbarError(`/auth/session`, this.getRequestHeader(), null);
	}

	logout(): Observable<any> {
		return this.apiHandlerService.post(`/auth/logout`, {}, this.getRequestHeader(), null);
	}

	login(email: string): Observable<any> {
		return this.apiHandlerService.get(`/user-by-email/${email}`, this.getRequestHeader(), null);
	}
	// -----

	// USERS
	users(params?: any): Observable<any> {
		return this.apiHandlerService.get(`/users`, this.getRequestHeader(), params);
	}

	usersV2(params?: any): Observable<any> {
		return this.apiHandlerService.get(`/v2/users`, this.getRequestHeader(), params);
	}

	addUser(data: any): Observable<any> {
		return this.apiHandlerService.postWithSnackbarError(`/user`, data, this.getRequestHeader(), null);
	}

	getUserInfoWorkday(email: any): Observable<any> {
		return this.apiHandlerService.getWithoutSnackbarError(`/users/workday/${email}`, this.getRequestHeader(), null);
	}

	getUser(id: string): Observable<any> {
		return this.apiHandlerService.get(`/user/${id}`, this.getRequestHeader(), null);
	}

	getUserByEmail(email: string): Observable<any> {
		return this.apiHandlerService.get(`/user-by-email/${email}`, this.getRequestHeader(), null);
	}

	getUserByStaffNumber(staffNumber: string): Observable<any> {
		return this.apiHandlerService.getWithSnackbarError(
			`/user-by-staff-number/${staffNumber}`,
			this.getRequestHeader(),
			null,
		);
	}

	updateUser(id: string, data: any): Observable<any> {
		return this.apiHandlerService.put(`/user/${id}`, data, this.getRequestHeader(), null);
	}

	deleteUser(id: string): Observable<any> {
		return this.apiHandlerService.delete(`/user/${id}`, this.getRequestHeader());
	}

	deleteUsers(id: string): Observable<any> {
		return this.apiHandlerService.deleteWithBody(`/user/${id}`, this.getRequestHeader(), { body: {} });
	}

	pilots(params?: any): Observable<any> {
		return this.apiHandlerService.get(`/users/last-updates`, this.getRequestHeader(), params);
	}

	uploadPilotData(staffNumber: string, data: any): Observable<any> {
		return this.apiHandlerService.post(`/user/${staffNumber}/document-upload`, data, this.getRequestHeader(), null);
	}

	getFileCounts(staffNumber: string): Observable<any> {
		return this.apiHandlerService.get(`/user/${staffNumber}/file-counts`, this.getRequestHeader());
	}

	getDocuments(params?: any): Observable<any> {
		return this.apiHandlerService.get(`/user/documents`, this.getRequestHeader(), params);
	}

	deleteDocument(fileId: string): Observable<any> {
		return this.apiHandlerService.deleteWithBody(`/user/documents/${fileId}`, this.getRequestHeader(), { body: {} });
	}
	// -----

	// PROFILES
	profiles(params?: any): Observable<any> {
		return this.apiHandlerService.get(`/profiles`, this.getRequestHeader(), params);
	}

	getProfile(id: string): Observable<any> {
		return this.apiHandlerService.get(`/profile/${id}`, this.getRequestHeader(), null);
	}

	getMySignOffPin(): Observable<any> {
		return this.apiHandlerService.get(`/profile/my-sign-off-pin`, this.getRequestHeader(), null);
	}

	updateProfile(id: string, data: any): Observable<any> {
		return this.apiHandlerService.put(`/profile/${id}`, data, this.getRequestHeader(), null);
	}

	deleteProfile(id: string): Observable<any> {
		return this.apiHandlerService.delete(`/profile/${id}`, this.getRequestHeader(), null);
	}

	uploadProfile(id: string, data: { file: File | Blob; type: string; prepared?: boolean }): Observable<any> {
		const file$ = data.prepared ? of(data.file as File) : from(prepareProfileImageFile(data.file, data.type));
		return file$.pipe(
			catchError((err) => this.apiHandlerService.handleClientError(err)),
			switchMap((file) => {
				const formData = new FormData();
				// Use fileType only — a field named "type" collides with File.type on iPad/Safari WebKit.
				formData.append("fileType", data.type);
				formData.append("file", file, file.name);
				return this.apiHandlerService.put(`/profile/upload/${id}`, formData, this.getRequestHeader(), null, "file");
			}),
		);
	}

	clearProfileSignature(id: string): Observable<any> {
		return this.apiHandlerService.deleteWithBody(`/profile/signature/${id}`, this.getRequestHeader(), { body: {} });
	}

	getTrainingProfileTypes(): Observable<any> {
		return this.apiHandlerService.get(`/profile/training-profile-types`, this.getRequestHeader(), null);
	}

	getTrainingProfile(id: string): Observable<any> {
		return this.apiHandlerService.get(`/profile/training-profile/${id}`, this.getRequestHeader(), null);
	}

	updateTrainingProfile(id: string, data: any): Observable<any> {
		return this.apiHandlerService.put(`/profile/training-profile/${id}`, data, this.getRequestHeader(), null);
	}
	// -----

	// RATINGS
	ratings(params?: any): Observable<any> {
		return this.apiHandlerService.get(`/ratings`, this.getRequestHeader(), params);
	}

	addRating(id: string, data: any): Observable<any> {
		return this.apiHandlerService.post(`/rating/${id}`, data, this.getRequestHeader(), null, "file");
	}

	getRating(id: string): Observable<any> {
		return this.apiHandlerService.get(`/rating/${id}`, this.getRequestHeader(), null);
	}

	uploadRatingSummary(candidateId: string, data: any): Observable<any> {
		const formData = new FormData();
		formData.append("file", data.file);

		return this.apiHandlerService.post(
			`/rating/${candidateId}/upload-summary`,
			formData,
			this.getRequestHeader(),
			null,
			"file",
		);
	}

	startRating(data: any): Observable<any> {
		return this.apiHandlerService.post(`/rating/start-rating`, data, this.getRequestHeader(), null);
	}

	addRemoveRatingObservation(traineeAssessmentId: string, data: any): Observable<any> {
		return this.apiHandlerService.post(
			`/rating/${traineeAssessmentId}/add-remove-observation`,
			data,
			this.getRequestHeader(),
			null,
		);
	}

	assessmentEvaluation(candidateId: string, data: any): Observable<any> {
		return this.apiHandlerService.post(
			`/rating/${candidateId}/assessment-evaluation`,
			data,
			this.getRequestHeader(),
			null,
		);
	}

	instructorOtherComments(data: any): Observable<any> {
		return this.apiHandlerService.post(`/rating/maneuver-comments`, data, this.getRequestHeader(), null);
	}

	resetOB(traineeAssessmentId: string, maneuverCode: string, data: any): Observable<any> {
		return this.apiHandlerService.put(
			`/rating/${traineeAssessmentId}/reset/${maneuverCode}`,
			data,
			this.getRequestHeader(),
			null,
		);
	}

	calculateRating(traineeAssessmentId: string, candidateId: string): Observable<any> {
		return this.apiHandlerService.post(
			`/rating/${traineeAssessmentId}/calculate/${candidateId}`,
			{},
			this.getRequestHeader(),
			null,
		);
	}

	instructorSignoff(traineeAssessmentId: string, data: any): Observable<any> {
		return this.apiHandlerService.postWithSignOffError(
			`/rating/${traineeAssessmentId}/instructor-sign-off`,
			data,
			this.getRequestHeader(),
			null,
		);
	}

	instructorSignoffValidation(data: any): Observable<any> {
		return this.apiHandlerService.postWithSignOffError(
			`/v2/rating/instructor-sign-off`,
			data,
			this.getRequestHeader(),
			null,
		);
	}

	stagingDataAPI(data: StagingData): Observable<any> {
		// Quiet so sign-off can tell a network failure from an HTTP error. The caller shows the dialog.
		return this.apiHandlerService.postQuiet(`/rating/staging`, data, this.getRequestHeader(), null);
	}

	instructorSignoffValidationQuiet(data: any): Observable<any> {
		return this.apiHandlerService.postQuiet(`/v2/rating/instructor-sign-off`, data, this.getRequestHeader(), null);
	}

	candidateSignatureValidationQuiet(data: any): Observable<any> {
		return this.apiHandlerService.postQuiet(`/v2/rating/candidate-sign-off`, data, this.getRequestHeader(), null);
	}

	candidateSignoff(traineeAssessmentId: string, data: any): Observable<any> {
		return this.apiHandlerService.postWithSignOffError(
			`/rating/${traineeAssessmentId}/sign-off`,
			data,
			this.getRequestHeader(),
			null,
		);
	}

	candidateSignature(staffNumber: string): Observable<any> {
		return this.apiHandlerService.get(`/user-by-staff-number/${staffNumber}`, this.getRequestHeader(), null);
	}

	getMySignature(): Observable<any> {
		return this.apiHandlerService.getWithoutSnackbarError(`/user/my-signature`, this.getRequestHeader(), null);
	}

	candidateSignatureValidation(data: any): Observable<any> {
		return this.apiHandlerService.postWithSignOffError(
			`/v2/rating/candidate-sign-off`,
			data,
			this.getRequestHeader(),
			null,
		);
	}

	userSignoff(data: LDAPSignOff): Observable<any> {
		return this.apiHandlerService.post(`/rating/ldapSignOff`, data, this.getRequestHeader(), null);
	}

	candidateCompleteRating(traineeAssessmentId: string, data: any): Observable<any> {
		return this.apiHandlerService.post(
			`/rating/${traineeAssessmentId}/complete-rating`,
			data,
			this.getRequestHeader(),
			null,
			"file",
		);
	}

	private sanitize(s: string) {
		return String(s)
			.trim()
			.replace(/[^\w.-]/g, "_");
	}

	private dataUrlToFile(dataUrl: string, fileName: string): File {
		const [meta, b64] = dataUrl.split(",");
		const mime = /data:(.*?);base64/.exec(meta)?.[1] || "application/octet-stream";
		const bin = atob(b64);
		const u8 = new Uint8Array(bin.length);
		for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
		return new File([u8], fileName, { type: mime });
	}

	private toFileLike(input: SigInput, fileName: string): File | Blob {
		if (input instanceof File || input instanceof Blob) return input;
		if (typeof input === "string" && input.startsWith("data:")) {
			return this.dataUrlToFile(input, fileName);
		}
		// fallback: raw string -> text blob (rare)
		return new Blob([input], { type: "application/octet-stream" });
	}

	/** Single POST /rating/complete with multiple trainee_signatures file parts (filename = <candidateId>.png). */
	completeRatingWithFiles(dto: CompleteRatingFilesDto): Observable<any> {
		const fd = new FormData();

		// Scalars
		fd.append("trainingAssessmentId", dto.trainingAssessmentId);
		fd.append("instructor_name", dto.instructor_name);
		// Always append instructor_license_number as a string (backend requires string type)
		const instructorLicenseNumber = dto.instructor_license_number != null ? String(dto.instructor_license_number) : "";
		fd.append("instructor_license_number", instructorLicenseNumber);

		// Trainee signatures — one part per candidate, filename <candidateId>.png
		// If backend needs brackets, change to: const listKey = 'trainee_signatures[]';
		const listKey = "trainee_signatures";
		for (const t of dto.trainees || []) {
			const filename = `${this.sanitize(t.candidateId)}.png`;
			let filePart;
			if (t.signature) {
				filePart = this.toFileLike(t.signature, filename); // accepts File/Blob or data-URL
				fd.append(listKey, filePart, filename);
			} else {
				filePart = null;
			}
		}

		// Instructor signature (binary)
		const insFile = this.toFileLike(dto.instructor_signature, "instructor_signature.png");
		fd.append("instructor_signature", insFile, (insFile as any).name ?? "instructor_signature.png");

		// IMPORTANT: do NOT set Content-Type; wrapper + reqType 'file' should handle boundary
		// Option A (safest): let wrapper build headers from reqType only
		return this.apiHandlerService.post("/rating/complete", fd, this.getRequestHeader(), null, "file");

		// Option B (if you must pass headers):
		// const hdrs = this.getRequestHeader?.();
		// if (hdrs?.has && hdrs.has('Content-Type')) hdrs.delete('Content-Type');
		// return this.post('/rating/complete', fd, hdrs, null, 'file');
	}

	sendToCandidate(dto: {
		trainingAssessmentId: string;
		instructor_name: string;
		instructor_license_number?: string;
		instructor_signature: File | Blob | string;
	}): Observable<any> {
		const fd = new FormData();
		fd.append("trainingAssessmentId", dto.trainingAssessmentId);
		fd.append("instructor_name", dto.instructor_name);
		fd.append("instructor_license_number", dto.instructor_license_number ?? "");
		const insFile = this.toFileLike(dto.instructor_signature, "instructor_signature.png");
		fd.append("instructor_signature", insFile, (insFile as any).name ?? "instructor_signature.png");
		return this.apiHandlerService.post("/rating/send-to-candidate", fd, this.getRequestHeader(), null, "file");
	}

	getCandidateChecklist(): Observable<any> {
		return this.apiHandlerService.get("/rating/candidate-checklist", this.getRequestHeader(), {
			role: this.roleService.getUserRole(),
		});
	}

	getCandidateChecklistPreview(): Observable<any> {
		return this.apiHandlerService.get("/rating/candidate-checklist/preview", this.getRequestHeader(), {
			role: this.roleService.getUserRole(),
		});
	}

	getCandidateChecklistDetail(trainingAssessmentId: string): Observable<any> {
		return this.apiHandlerService.getWithoutSnackbarError(
			`/rating/candidate-checklist/${trainingAssessmentId}`,
			this.getRequestHeader(),
			{ role: this.roleService.getUserRole() },
		);
	}

	listFeedbackTemplates(params?: {
		page?: number;
		size?: number;
		search?: string;
		formType?: string;
		role?: string;
		aocId?: string;
		aocIds?: string[];
		startDate?: string;
		endDate?: string;
	}): Observable<any> {
		return this.apiHandlerService.get("/feedback/templates", this.getRequestHeader(), params);
	}

	getFeedbackTemplate(id: string, params?: { role?: string; aocId?: string; aocIds?: string[] }): Observable<any> {
		return this.apiHandlerService.get(`/feedback/templates/${id}`, this.getRequestHeader(), params);
	}

	createFeedbackTemplate(dto: FeedbackTemplateRequest): Observable<any> {
		return this.apiHandlerService.postQuiet("/feedback/templates", dto, this.getRequestHeader(), null);
	}

	updateFeedbackTemplate(id: string, dto: FeedbackTemplateRequest): Observable<any> {
		return this.apiHandlerService.putQuiet(`/feedback/templates/${id}`, dto, this.getRequestHeader(), null);
	}

	deleteFeedbackTemplate(id: string): Observable<any> {
		// Fastify rejects DELETE with Content-Type application/json and an empty body.
		return this.apiHandlerService.deleteWithBody(`/feedback/templates/${id}`, this.getRequestHeader(), {
			body: {},
		});
	}

	listFeedbackResponses(
		templateId: string,
		params?: {
			page?: number;
			size?: number;
			search?: string;
			role?: string;
			aocId?: string;
			aocIds?: string[];
			startDate?: string;
			endDate?: string;
		},
	): Observable<any> {
		const query = new URLSearchParams();
		if (params?.page) query.set("page", String(params.page));
		if (params?.size) query.set("size", String(params.size));
		if (params?.search) query.set("search", params.search);
		if (params?.startDate) query.set("startDate", params.startDate);
		if (params?.endDate) query.set("endDate", params.endDate);
		if (params?.role) query.set("role", params.role);
		if (params?.aocIds?.length) {
			params.aocIds.forEach((aocId) => query.append("aocIds", aocId));
		} else if (params?.aocId) {
			query.set("aocId", params.aocId);
		}
		const suffix = query.toString() ? `?${query.toString()}` : "";
		return this.apiHandlerService.get(`/feedback/templates/${templateId}/responses${suffix}`, this.getRequestHeader());
	}

	getFeedbackResponse(
		templateId: string,
		submissionId: string,
		params?: { role?: string; aocId?: string; aocIds?: string[] },
	): Observable<any> {
		return this.apiHandlerService.get(
			`/feedback/templates/${templateId}/responses/${submissionId}`,
			this.getRequestHeader(),
			params,
		);
	}

	getFeedbackTask(traineeAssessmentId: string): Observable<any> {
		return this.apiHandlerService.get(`/feedback/tasks/${traineeAssessmentId}`, this.getRequestHeader());
	}

	getStandaloneFeedbackTask(taskId: string): Observable<any> {
		return this.apiHandlerService.get(`/feedback/tasks/standalone/${taskId}`, this.getRequestHeader(), {
			role: this.roleService.getUserRole(),
		});
	}

	dispatchFeedbackTemplate(id: string): Observable<any> {
		return this.apiHandlerService.post(`/feedback/templates/${id}/dispatch`, {}, this.getRequestHeader(), null);
	}

	listMyFeedback(params?: {
		page?: number;
		size?: number;
		search?: string;
		kind?: "standalone" | "tied";
		role?: string;
		startDate?: string;
		endDate?: string;
	}): Observable<any> {
		const query: Record<string, string | number> = {};
		if (params?.page) query["page"] = params.page;
		if (params?.size) query["size"] = params.size;
		if (params?.search) query["search"] = params.search;
		if (params?.kind) query["kind"] = params.kind;
		if (params?.role) query["role"] = params.role;
		if (params?.startDate) query["startDate"] = params.startDate;
		if (params?.endDate) query["endDate"] = params.endDate;
		return this.apiHandlerService.get("/feedback/submissions/mine", this.getRequestHeader(), query);
	}

	getMyFeedback(submissionId: string, role?: string): Observable<any> {
		const query = role ? { role } : undefined;
		return this.apiHandlerService.get(`/feedback/submissions/mine/${submissionId}`, this.getRequestHeader(), query);
	}

	submitFeedback(dto: {
		taskId: string;
		answers: Array<{ questionId: string; rating?: number; remarks?: string }>;
	}): Observable<any> {
		return this.apiHandlerService.post("/feedback/submissions", dto, this.getRequestHeader(), null);
	}

	candidateRemoteSignOff(dto: {
		trainingAssessmentId: string;
		secretKey: string;
		staffNumber: string;
		trainee_signature: File | Blob | string;
	}): Observable<any> {
		const fd = new FormData();
		fd.append("trainingAssessmentId", dto.trainingAssessmentId);
		fd.append("secretKey", dto.secretKey);
		fd.append("staffNumber", dto.staffNumber);
		const sigFile = this.toFileLike(dto.trainee_signature, "trainee_signature.png");
		fd.append("trainee_signature", sigFile, (sigFile as any).name ?? "trainee_signature.png");
		return this.apiHandlerService.postWithSignOffError(
			"/rating/candidate-remote-sign-off",
			fd,
			this.getRequestHeader(),
			null,
			"file",
		);
	}

	/** Single POST /user/{staffnumber}/document-upload with multiple pilot-related file parts (filename = /StaffID_Name/<Selected_Folder>/filename.pdf). */
	pilotDataWithFiles(dto: PilotDataFilesDto): Observable<any> {
		const fd = new FormData();

		fd.append("staffNumber", dto.staffNumber);
		fd.append("category", dto.category);

		const filename = dto.filename || dto.file.name;
		const filePart = this.toFileLike(dto.file, filename);
		fd.append("file", filePart, filename);

		if (dto.filename) {
			fd.append("filename", dto.filename);
		}

		return this.apiHandlerService.postWithSnackbarError(
			`/user/${dto.staffNumber}/document-upload`,
			fd,
			this.getRequestHeader(),
			null,
			"file",
		);
	}

	temCommentRating(id: string, data: any): Observable<any> {
		return this.apiHandlerService.post(`/rating/${id}/tem-comment`, data, this.getRequestHeader(), null);
	}

	getSessions(params: any): Observable<any> {
		const { formId } = params;
		return this.apiHandlerService.get(`/rating/${formId}/sessions`, this.getRequestHeader(), params);
	}

	getSummary(candidateId: string, params: { role: string }): Observable<any> {
		return this.apiHandlerService.get(`/rating/${candidateId}/summary`, this.getRequestHeader(), params);
	}

	uploadSummary(candidateId: string, data: any): Observable<any> {
		return this.apiHandlerService.post(
			`/rating/${candidateId}/upload-summary`,
			data,
			this.getRequestHeader(),
			null,
			"file",
		);
	}

	generateSummary(candidateId: string, params: { downloadOnly: boolean; role: string }): Observable<any> {
		return this.apiHandlerService.get(`/rating/${candidateId}/generate-summary`, this.getRequestHeader(), params);
	}

	getDownloadSummary(candidateId: string): Observable<any> {
		return this.apiHandlerService.get(`/rating/${candidateId}/download-summary`, this.getRequestHeader(), null);
	}
	// -----

	// FORMS

	formsMasterValues(): Observable<any> {
		return this.apiHandlerService.get(`/form/master-types`, this.getRequestHeader(), null);
	}

	forms(params?: any): Observable<any> {
		return this.apiHandlerService.get("/forms-admin", this.getRequestHeader(), params);
	}

	formPublish(formId: string): Observable<any> {
		return this.apiHandlerService.post(`/form/publish/${formId}`, { formId }, this.getRequestHeader(), null);
	}

	formUnPublish(formId: string): Observable<any> {
		return this.apiHandlerService.put(`/form/unpublish/${formId}`, { formId }, this.getRequestHeader(), null);
	}

	formRemove(formId: string): Observable<any> {
		return this.apiHandlerService.deleteWithBody(`/form/${formId}`, this.getRequestHeader(), { body: { formId } });
	}

	reports(params?: any, role?: string): Observable<any> {
		const route =
			role === Roles.ADMIN || role === Roles.GROUP_ADMIN
				? "/forms-report-admin"
				: role === Roles.OFFICE_ADMIN
					? "/forms-report-admin-office"
					: role === Roles.PILOT || role === Roles.CABIN_CREW
						? "/forms-report-pilot"
						: "/forms-report-instructor";

		return this.apiHandlerService.get(route, this.getRequestHeader(), params);
	}

	instructorFormsRating(params?: any): Observable<any> {
		return this.apiHandlerService.get("/forms-rating-instructor", this.getRequestHeader(), params);
	}

	getFormConfig(dropdownProperty?: any, sessionVersion?: number, aocCode?: string): Observable<any> {
		const params = sessionVersion ? { sessionVersion, ...(aocCode ? { aocCode } : {}) } : null;
		return this.apiHandlerService.get(`/v2/form/form-types/${dropdownProperty.code}`, this.getRequestHeader(), params);
	}

	addForm(data: TS1FormRequestJSON): Observable<any> {
		return this.apiHandlerService.post(`/form`, data, this.getRequestHeader(), null);
	}

	getForm(id: string): Observable<any> {
		return this.apiHandlerService.get(`/form/${id}`, this.getRequestHeader(), null);
	}

	udateForm(id: string, data: any): Observable<any> {
		return this.apiHandlerService.put(`/form/${id}`, data, this.getRequestHeader(), null);
	}

	deleteForm(id: string): Observable<any> {
		return this.apiHandlerService.delete(`/form/${id}`, this.getRequestHeader(), null);
	}

	deleteFormInStaging(trainingAssessmentId: string): Observable<any> {
		return this.apiHandlerService.deleteWithBody(
			`/rating/staging-assessment/${trainingAssessmentId}`,
			this.getRequestHeader(),
			{ body: {} },
		);
	}

	getFormInStaging(params?: { role?: string }): Observable<any> {
		const query = params?.role ? { role: params.role } : undefined;
		// Best-effort pull. Callers fall back to IndexedDB and must not open the server-error modal.
		return this.apiHandlerService.getWithoutSnackbarError(
			`/rating/instructor-staging-assessment`,
			this.getRequestHeader(),
			query,
		);
	}

	getFormSessionConfigs(): Observable<any> {
		return this.apiHandlerService.get(`/form/session-configs`, this.getRequestHeader(), null);
	}

	getFormObservations(): Observable<any> {
		return this.apiHandlerService.get(`/form/observations`, this.getRequestHeader(), null);
	}

	getFormManeuvers(): Observable<any> {
		return this.apiHandlerService.get(`form/maneuvers`, this.getRequestHeader(), null);
	}

	getFormTypes(): Observable<any> {
		return this.apiHandlerService.get(`form/form-types`, this.getRequestHeader(), null);
	}

	getByStaffNumber(staffNumber: string, roles?: string): Observable<any> {
		const params = roles ? { roles } : null;
		return this.apiHandlerService.getWithoutSnackbarError(
			`/user-by-staff-number/${staffNumber}`,
			this.getRequestHeader(),
			params,
		);
	}

	getCommentsById(query: { candidate_id: string; maneuver_code: string }): Observable<any> {
		return this.apiHandlerService.get(
			`/rating/maneuver-remark/${query.candidate_id}/${query.maneuver_code}`,
			this.getRequestHeader(),
			null,
		);
	}

	updateOBComment(commentId: string, editedComment: { description: string; candidateRole: string }): Observable<any> {
		return this.apiHandlerService.put(
			`/rating/observation-comments/${commentId}`,
			editedComment,
			this.getRequestHeader(),
			null,
		);
	}

	updateManeuverComment(commentId: string, editedComment: { description: string }): Observable<any> {
		return this.apiHandlerService.put(
			`/rating/maneuver-comments/${commentId}`,
			editedComment,
			this.getRequestHeader(),
			null,
		);
	}

	updateTEMComment(
		candidateId: string,
		maneuverCode: any,
		data: { remakrs: string; candidateRole: string },
	): Observable<any> {
		return this.apiHandlerService.patch(
			`/rating/tem-remarks/${candidateId}/${maneuverCode}`,
			data,
			this.getRequestHeader(),
			null,
		);
	}
	// -----

	private getRequestHeader() {
		return {
			headers: new HttpHeaders(),
		};
	}

	// draft
	pendingRatings<T = any>(params?: any) {
		return this.apiHandlerService.get(`/draft/pending`, this.getRequestHeader(), params) as unknown as Observable<
			APIResponse<T>
		>;
	}

	deletePendingSession(traineeAsessmentId: string) {
		return this.apiHandlerService.delete(`/draft/${traineeAsessmentId}`, this.getRequestHeader());
	}

	syncDraft(data: any) {
		return this.apiHandlerService.post(`/draft`, data, this.getRequestHeader(), null);
	}

	geminiChat(messages: Array<{ role: "user" | "model"; text: string }>) {
		return this.apiHandlerService.post(
			`/gemini/chat`,
			{ messages },
			this.getRequestHeader(),
			null,
		) as unknown as Observable<{ data: { text: string } }>;
	}

	geminiRephrase(text: string) {
		return this.apiHandlerService.post(
			`/gemini/rephrase`,
			{ text },
			this.getRequestHeader(),
			null,
		) as unknown as Observable<{ data: { text: string } }>;
	}
}
